import { loadModules } from 'esri-loader';
import { GlobalConstants } from 'src/app/constant/constant';
import { BoundaryOptions } from './../models/form-definition/form-control-options';
import { Injectable, isDevMode } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Platform, ModalController } from '@ionic/angular';
import { Geolocation, PermissionStatus, Position } from '@capacitor/geolocation';
import { BehaviorSubject, of, ReplaySubject, Subject } from 'rxjs';
import { RepositoryService } from '../baseServices/repository.service';
import { StoreName } from '../entities/StoreName';
import { Capacitor } from '@capacitor/core';
import { Coordinate, ESRIAddress, Layer, LocationCaptureMethod } from 'src/app/models/location/location-models';
import { DeviceInfoService } from './device-info.service';
import * as haversine from 'haversine';
import { CoordinatesDto } from '../dtos/location-service-dtos';
import { convertAccuracyInMetersToFeet } from '../utils/coordinate.util';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  onLocationChange: Subject<Coordinate> = new ReplaySubject<Coordinate>();

  private locationStorageKey = 'geolocation';

  constructor(
    private http: HttpClient,
    public platform: Platform,
    private deviceInfo: DeviceInfoService,
    private modalController: ModalController
  ) {}

  async getBoundaryData(config: BoundaryOptions, coordinate: Coordinate) {
    const outFields = [...GlobalConstants.STANDARD_OUTFIELDS, ...config.customOutFields];

    let queryParams: HttpParams = new HttpParams();
    queryParams = queryParams.append('where', config.whereClause);
    queryParams = queryParams.append(
      'geometry',

      JSON.stringify(this.latLonToMercator(coordinate.latitude, coordinate.longitude))
    );
    queryParams = queryParams.append('geometryType', 'esriGeometryPoint');
    queryParams = queryParams.append('inSR', '102100');
    queryParams = queryParams.append('outSR', '102100');
    queryParams = queryParams.append('spatialRel', 'esriSpatialRelIntersects');
    queryParams = queryParams.append('outFields', outFields.toString());
    queryParams = queryParams.append('returnGeometry', 'false');
    queryParams = queryParams.append('f', 'pjson');
    queryParams = queryParams.append('token', config.apiKey);

    const headers = new HttpHeaders({
      'Access-Control-Allow-Methods': 'GET, POST',
      'Access-Control-Allow-Origin': '*',
      Accept: 'application/json',
      'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
      'Request-Type': 'arcgis',
    });

    const response = await this.http
      .get(config.layerUrl, {
        params: queryParams,
        headers,
        observe: 'response',
      })
      .toPromise();

    return this.processBoundaryData(response.body);
  }

  areCoordsWithinRadius(checkPoint, centerPoint, km = 0.1) {
    if (centerPoint) {
      const ky = 40000 / 360;
      const kx = Math.cos((Math.PI * centerPoint.latitude) / 180.0) * ky;
      const dx = Math.abs(centerPoint.longitude - checkPoint.longitude) * kx;
      const dy = Math.abs(centerPoint.latitude - checkPoint.latitude) * ky;
      return Math.sqrt(dx * dx + dy * dy) <= km;
    }

    return true;
  }

  getDistanceBetweenCoordinates(coord1: { latitude: number; longitude: number }, coord2: { latitude: number; longitude: number }, thresholdInFeet?: number) {
    let isWithin = true;

    if (thresholdInFeet) {
      isWithin = haversine(coord1, coord2, {
        unit: 'meter',
        threshold: thresholdInFeet * GlobalConstants.FOOT_TO_METER_CONVERSION_RATE,
      });
    }

    const distance = haversine(coord1, coord2, { unit: 'meter' });

    return { isWithin, distance: convertAccuracyInMetersToFeet(distance) };
  }

  async getAddressByCoordinates(coordinates: { lat: number; lng: number }): Promise<ESRIAddress | undefined> {
    return loadModules(['esri/tasks/Locator', 'esri/geometry/Point', 'esri/config'])
      .then(async ([Locator, Point, esriConfig]) => {
        esriConfig.apiKey = environment.esri.apiKey;
        try {
          const point = new Point({
            x: coordinates.lng,
            y: coordinates.lat,
          });

          const locator = new Locator({
            url: GlobalConstants.ESRI_LOCATOR_URL,
          });

          const results = await locator.locationToAddress({
            location: point,
            distance: 100,
          });

          return results;
        } catch (err) {
          return undefined;
        }
      })
      .catch(err => {
        return undefined;
      });
  }

  async getCurrentStateName(): Promise<string | null> {
    let currentLocation = await this.getCurrentLocation();
    let address = await this.getAddressByCoordinates({lat: currentLocation.coords.latitude, lng: currentLocation.coords.longitude});
    if (address) {
      return address.attributes.Region;
    }
    else {
      return null;
    }
  }

  async getLocationPermission(): Promise<string> {
    var permission = await Geolocation.checkPermissions()
      .then(permission => {
        return permission.location;
      })
      .catch(error => {
        //if browser has not implemented Permission API (e.g., iOS browsers < iOS 16), checkPermissions() will throw an error
        //try to fetch location without checking permission
        const options = {
          enableHighAccuracy: true,
          timeout: 1000,
          maximumAge: 0,
        };
        return Geolocation.getCurrentPosition(options)
          .then(result => 'granted')
          .catch(error => 'denied');
      });

    let platform = await this.deviceInfo.getPlatform();

    // NOTE: Temporarily allowing web without permission check due to corporate policy on Tetra Tech PCs disabling device-wide location services
    // This should be removed if corporate reverses their policy
    // Or otherwise a more robust solution should be implemented
    // Coordinates will be (0,0) if location services are disabled
    if (platform === 'web') { 
      permission = 'granted';
    }

    return permission;
  }

  /**
   * Ensures location permission is granted. If not, opens a modal prompting the user.
   * Returns true if permission is granted after the prompt; false otherwise.
   */
  async ensureLocationPermissionOrPrompt(): Promise<boolean> {
    const permission = await this.getLocationPermission();
    if (permission === 'granted') {
      return true;
    }

    const { LocationPermissionRequiredPage } = await import('src/app/pages/locationPermissionRequired/location-permission-required');
    const modal = await this.modalController.create({
      component: LocationPermissionRequiredPage,
      backdropDismiss: false,
      cssClass: 'base-modal',
    });

    await modal.present();
    await modal.onDidDismiss();

    const finalPermission = await this.getLocationPermission();
    return finalPermission === 'granted';
  }

  async checkLocationPermissionGranted(): Promise<boolean> {
    const permission = await this.getLocationPermission();
    return permission === 'granted';
  }

  async requestLocationPermission(): Promise<PermissionStatus> {
    try {
      if (Capacitor.getPlatform() === 'web') {
        return new Promise((resolve, reject) => this.getLocationFromBrowser(resolve, reject));
      } else {
        return Geolocation.requestPermissions();
      }
    } catch (err) {
      console.log(err);
    }
  }

  getStoredCurrentLocation() {
    const savedLocation = localStorage.getItem(this.locationStorageKey);
    if (savedLocation) {
      return JSON.parse(savedLocation);
    }
  }

  getStoredCurrentLocationAsPosition() {
    const savedLocation = localStorage.getItem(this.locationStorageKey);
    if (savedLocation) {
      const location = JSON.parse(savedLocation);
      return { coords: { latitude: location.latitude, longitude: location.longitude, altitude: location.altitude } };
    }
  }

  async getCurrentLocation(skipStoredLocation = false): Promise<Position> {
    const hasPermission = await this.checkLocationPermissionGranted();
    if (!hasPermission) {
      const defaultPosition: Position = {
        coords: {
          latitude: 0,
          longitude: 0,
          accuracy: null,
          altitudeAccuracy: 0,
          altitude: 0,
          speed: 0,
          heading: 0,
        },
        timestamp: 0,
      };
      return Promise.resolve(defaultPosition);
    }
    const options: PositionOptions = {
      enableHighAccuracy: true,
      maximumAge: 0,
    };
    const defaultPosition: Position = {
      coords: {
        latitude: 0,
        longitude: 0,
        accuracy: null,
        altitudeAccuracy: 0,
        altitude: 0,
        speed: 0,
        heading: 0,
      },
      timestamp: 0,
    };

    try {
      const timeoutPromise = new Promise<Position>((resolve, reject) => {
        setTimeout(() => {
          if (!skipStoredLocation) {
            const storedPosition = this.getStoredCurrentLocation();
            if (storedPosition) {
              defaultPosition.coords.latitude = storedPosition.latitude;
              defaultPosition.coords.longitude = storedPosition.longitude;
              defaultPosition.coords.altitude = storedPosition.altitude;
              defaultPosition.coords.accuracy = storedPosition.accuracy;
            }
          }
          resolve(defaultPosition);
        }, GlobalConstants.GPS_MAX_WAIT_TIME);
      });
     
      const locationPromise = Geolocation.getCurrentPosition(options);

      return await Promise.race([timeoutPromise, locationPromise]);
    } catch (err) {
      if (err.code === 1) {
        console.warn('User denied geolocation permissions');
      } else {
        console.warn('Location error:', err);
      }
      return Promise.resolve(defaultPosition);;
    }
  }

  async getCurrentCoordinates(): Promise<CoordinatesDto> {
    const position = await this.getCurrentLocation();
    return {
      Latitude: position.coords.latitude,
      Longitude: position.coords.longitude
    };
  }

  async getCoarseCoordinates(): Promise<CoordinatesDto> {
    const hasPermission = await this.checkLocationPermissionGranted();
    if (!hasPermission) {
      return { Latitude: 0, Longitude: 0 };
    }
    const options: PositionOptions = {
      enableHighAccuracy: false,
      maximumAge: 0,
      timeout: GlobalConstants.GPS_MAX_WAIT_TIME
    };

    try {
      const position = await new Promise<Position>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });
      return {
        Latitude: position.coords.latitude,
        Longitude: position.coords.longitude
      };
    } catch (err: any) {
      if (err.code === 1) {
        console.warn('User denied coarse geolocation permissions');
      } else {
        console.warn('Coarse coarse location error:', err);
      }
      return {
        Latitude: 0,
        Longitude: 0
      };;
    }
  }

  /** 
   * @deprecated Use LocationTrackingService instead. 
  * Form system seems to reference this but ultimately not do anything with it.
  */
  async trackDistance(timeout = 10000): Promise<string> {
    const watch = Geolocation.watchPosition({ timeout }, (coordinates, err) => {
      if (coordinates) {
        const point = {
          latitude: coordinates.coords.latitude,
          longitude: coordinates.coords.longitude,
          altitude: coordinates.coords.altitude,
          accuracy: coordinates.coords.accuracy,
        };

        this.onLocationChange.next({
          latitude: point.latitude,
          longitude: point.longitude,
          accuracy: point.accuracy,
          altitude: point.altitude,
          datetime: '',
          captureMethod: LocationCaptureMethod.Locator
        });
      }

      if (err) {
        console.log('error: ', err);
      }
    });

    return watch;
  }

  /** 
   * @deprecated Use LocationTrackingService instead. 
  * Form system seems to reference this but ultimately not do anything with it.
  */
  async stopTrack(watchId: string): Promise<void> {
    await Geolocation.clearWatch({ id: watchId });
  }

  private getLocationFromBrowser(resolve, reject) {
    if (navigator) {
      const options = {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      };
      Geolocation.getCurrentPosition(options)
        .then(
          resp => {
            //set the location information in location storage
            const geoLocationData = {
              longitude: resp.coords.longitude ?? 0,
              latitude: resp.coords.latitude ?? 0,
              altitude: resp.coords.altitude ?? 0,
            };
            localStorage.setItem(this.locationStorageKey, JSON.stringify(geoLocationData));
            localStorage.setItem('gpsAccuracyInMeters', JSON.stringify(resp.coords.accuracy));
            resolve({
              lng: resp.coords.longitude ?? 0,
              lat: resp.coords.latitude ?? 0,
              alt: resp.coords.altitude ?? 0,
            });
          },
          err => {
            const geoLocationData = {
              longitude: 0,
              latitude: 0,
              altitude: 0,
            };
            localStorage.setItem(this.locationStorageKey, JSON.stringify(geoLocationData));
            console.log(err);
            reject(err);
          }
        )
        .catch(err => {
          const geoLocationData = {
            longitude: 0,
            latitude: 0,
            altitude: 0,
          };
          localStorage.setItem(this.locationStorageKey, JSON.stringify(geoLocationData));
          reject(err);
        });
    } else {
      reject('Error while accessing geoLocation');
    }
  }

  private latLonToMercator(lat: number, lon: number) {
    if (isDevMode()) {
      lat = 26.22;
      lon = -81.763;
    }

    const rMajor = 6378137; //Equatorial Radius, WGS84
    const shift = Math.PI * rMajor;
    const x = (lon * shift) / 180;
    let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
    y = (y * shift) / 180;

    return { X: x, Y: y };
  }

  private processBoundaryData(data: any) {
    const { features } = data;
    const result: Layer[] = [];

    if (features && features.length) {
      features.forEach(feature => result.push({ ...feature.attributes }));
    }

    const mappedLayers = result.map(layer => ({
      [`Layer${layer.LayerType}`]: layer,
    }));

    return mappedLayers;
  }
}
